import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, Clock, User, RotateCcw, MessageSquare, ListTodo, Download, Loader2 } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '../components/ui/context-menu';
// Context menu is used properly within the review dialog
import { RichTextDisplay } from '../components/ui/rich-text-editor';
import LinkifiedText from '../components/LinkifiedText';
import api from '../lib/api';
import { toast } from 'sonner';

export default function FormSubmissions() {
  const navigate = useNavigate();
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [commonWorkspaces, setCommonWorkspaces] = useState([]);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [selectedText, setSelectedText] = useState('');
  const [createTaskFromTextOpen, setCreateTaskFromTextOpen] = useState(false);
  const [quickTaskTitle, setQuickTaskTitle] = useState('');
  const [creatingQuickTask, setCreatingQuickTask] = useState(false);

  // Handle text selection for creating tasks
  const handleTextSelection = useCallback(() => {
    setTimeout(() => {
      const selection = window.getSelection();
      const text = selection?.toString().trim();
      if (text && text.length > 0) {
        setSelectedText(text);
      } else {
        setSelectedText('');
      }
    }, 10);
  }, []);

  // Listen for selection changes when review dialog is open
  useEffect(() => {
    if (!reviewOpen) return;
    
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      const text = selection?.toString().trim();
      if (text && text.length > 0) {
        setSelectedText(text);
      } else {
        setSelectedText('');
      }
    };
    
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [reviewOpen]);

  const openCreateTaskFromTextDialog = () => {
    if (selectedText && selected) {
      // Use the full task dialog with the selected text
      openCreateTask(selected, selectedText.substring(0, 100));
    }
  };

  // Kept for backwards compatibility but not used directly anymore
  const createQuickTask = async () => {
    if (!quickTaskTitle.trim()) {
      toast.error('Task title is required');
      return;
    }

    setCreatingQuickTask(true);
    try {
      await api.post('/tasks', {
        title: quickTaskTitle,
        description: selectedText.length > 100 ? selectedText : '',
        priority: 'medium',
        status: 'pending'
      });
      toast.success('Task created successfully');
      setCreateTaskFromTextOpen(false);
      setSelectedText('');
      setQuickTaskTitle('');
    } catch (error) {
      toast.error('Failed to create task');
    } finally {
      setCreatingQuickTask(false);
    }
  };

  useEffect(function() {
    loadData();
  }, [formId, statusFilter]);

  const loadData = async () => {
    try {
      const url = '/forms/' + formId + '/submissions' + (statusFilter !== 'all' ? '?status=' + statusFilter : '');
      const res = await api.get(url);
      setSubmissions(res.data.submissions || []);
      setForm(res.data.form);
      setIsOwner(res.data.is_owner || false);
    } catch (err) {
      toast.error('Failed to load');
      navigate('/forms');
    } finally {
      setLoading(false);
    }
  };

  const openReview = (sub) => { setSelected(sub); setComment(''); setReviewOpen(true); };

  const doReview = async (action) => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post('/forms/' + formId + '/submissions/' + selected.submission_id + '/review', { action: action, comment: comment });
      toast.success(action === 'approve' ? 'Approved!' : action === 'reject' ? 'Rejected' : 'Sent back for changes');
      setReviewOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to review');
    } finally {
      setBusy(false);
    }
  };

  const openCreateTask = async (submission, text) => {
    setSelected(submission);
    setSelectedText(text || '');
    setTaskTitle(text || 'Task from ' + (form ? form.name : 'Form'));
    setTaskDescription(text && text.length > 100 ? text : '');
    setTaskDueDate(new Date().toISOString().split('T')[0]);
    setSelectedWorkspace('');
    setSelectedProject('');
    setAssignedTo('');
    setWorkspaceMembers([]);
    try {
      const res = await api.get('/users/common-workspaces/' + submission.submitted_by);
      setCommonWorkspaces(res.data.workspaces || []);
    } catch (err) {
      setCommonWorkspaces([]);
    }
    setTaskDialogOpen(true);
  };

  const loadWorkspaceMembers = async (wsId) => {
    if (!wsId) { setWorkspaceMembers([]); return; }
    try {
      const res = await api.get('/workspaces/' + wsId + '/members');
      setWorkspaceMembers(res.data.members || res.data || []);
    } catch (err) {
      setWorkspaceMembers([]);
    }
  };

  const handleCreateTask = async () => {
    if (!taskTitle.trim()) { toast.error('Task title is required'); return; }
    if (!selectedWorkspace) { toast.error('Please select a workspace'); return; }
    setBusy(true);
    try {
      await api.post('/forms/' + formId + '/submissions/' + selected.submission_id + '/create-task', {
        form_id: formId, submission_id: selected.submission_id, workspace_id: selectedWorkspace,
        project_id: selectedProject || null, title: taskTitle, description: taskDescription,
        due_date: taskDueDate || null, assigned_to: assignedTo || null, selected_text: selectedText
      });
      toast.success('Task created successfully!');
      setTaskDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create task');
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/forms/' + formId + '/export');
      const data = response.data;
      const csvRows = [data.columns.join(',')];
      data.rows.forEach(function(row) {
        const rowData = data.columns.map(function(col) {
          const val = row[col] || '';
          if (typeof val === 'string' && (val.indexOf(',') >= 0 || val.indexOf('"') >= 0)) return '"' + val.replace(/"/g, '""') + '"';
          return val;
        });
        csvRows.push(rowData.join(','));
      });
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = data.form_name.replace(/\s+/g, '_') + '_submissions.csv';
      link.click();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'approved') return <Badge className="bg-green-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Approved</Badge>;
    if (status === 'rejected') return <Badge variant="destructive" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Rejected</Badge>;
    if (status === 'sent_back') return <Badge variant="outline" className="border-amber-500 text-amber-600 flex items-center gap-1"><RotateCcw className="w-3 h-3" />Sent Back</Badge>;
    return <Badge variant="secondary" className="flex items-center gap-1"><Clock className="w-3 h-3" />Pending</Badge>;
  };

  const fieldName = (id) => form && form.fields ? (form.fields.find(function(f) { return f.field_id === id; }) || {}).label || id : id;
  const fmt = (v) => Array.isArray(v) ? v.join(', ') : (v ? v.toString() : '-');

  if (loading) return <AppLayout><div className="p-6 text-center text-muted-foreground">Loading...</div></AppLayout>;

  return (
    <AppLayout>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={function() { navigate('/forms'); }}><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h1 className="text-2xl font-bold">{form ? form.name : 'Form'} - Submissions</h1>
              <p className="text-sm text-muted-foreground">{submissions.length} total</p>
            </div>
          </div>
          {isOwner && <Button variant="outline" onClick={handleExport}><Download className="w-4 h-4 mr-2" />Export CSV</Button>}
        </div>

        <Tabs value={statusFilter} onValueChange={setStatusFilter} className="mb-6">
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="pending_approval">Pending</TabsTrigger>
            <TabsTrigger value="approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
            <TabsTrigger value="sent_back">Sent Back</TabsTrigger>
          </TabsList>
        </Tabs>

        {submissions.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">No submissions found</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {submissions.map(function(sub) {
              const isFeedbackForm = form && form.form_type === 'feedback';
              return (
                <Card key={sub.submission_id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={function() { openReview(sub); }} data-testid={'submission-' + sub.submission_id}>
                  <CardContent className="py-4">
                    <div className="flex justify-between items-start">
                      <div className="flex gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#4A9CC7]/10 flex items-center justify-center"><User className="w-5 h-5 text-[#4A9CC7]" /></div>
                        <div>
                          <p className="font-medium">{sub.submitted_by_name}</p>
                          {isFeedbackForm && sub.receiver_name && (
                            <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">
                              Feedback for: {sub.receiver_name}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {new Date(sub.created_at).toLocaleString()}
                            {sub.approver_name && ' - Approver: ' + sub.approver_name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {sub.is_receiver && <Badge variant="outline" className="text-xs border-purple-500 text-purple-600">About You</Badge>}
                        {sub.is_manager_view && <Badge variant="outline" className="text-xs border-blue-500 text-blue-600">Team Member</Badge>}
                        {sub.can_review && <Badge variant="outline" className="text-xs">Action Required</Badge>}
                        {getStatusBadge(sub.status)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Review Submission</DialogTitle></DialogHeader>
            {selected && (
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#4A9CC7]/10 flex items-center justify-center"><User className="w-5 h-5 text-[#4A9CC7]" /></div>
                    <div>
                      <p className="font-medium">{selected.submitted_by_name}</p>
                      {form && form.form_type === 'feedback' && selected.receiver_name && (
                        <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">
                          Feedback for: {selected.receiver_name}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">{new Date(selected.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  {getStatusBadge(selected.status)}
                </div>
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground">Responses</h4>
                  {form && form.fields && form.fields
                    .filter(function(f) {
                      // Hide user_selector fields for receivers/managers (they only need to see feedback content)
                      if (f.field_type === 'user_selector') {
                        // Show only if form owner or if we have the response (means backend allowed it)
                        return isOwner || (selected.responses && selected.responses[f.field_id] !== undefined);
                      }
                      return true;
                    })
                    .map(function(f) {
                    const value = selected.responses ? selected.responses[f.field_id] : null;
                    const isRichText = f.field_type === 'richtext';
                    
                    return (
                      <div 
                        key={f.field_id}
                        className="border rounded-lg p-3 hover:bg-muted/50 cursor-pointer group" 
                        onDoubleClick={function() { 
                          var val = fmt(value); 
                          if (val && val !== '-') openCreateTask(selected, val); 
                        }}
                        onMouseUp={handleTextSelection}
                        title="Double-click to create task, or select text and use the button"
                      >
                        <p className="text-xs text-muted-foreground mb-1">{f.label}</p>
                        {isRichText && value ? (
                          <RichTextDisplay content={value} className="font-medium" />
                        ) : (
                          <LinkifiedText 
                            text={fmt(value)} 
                            className="font-medium whitespace-pre-wrap" 
                            as="p"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                {selected.review_comments && selected.review_comments.length > 0 && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-sm text-muted-foreground mb-2 flex items-center gap-2"><MessageSquare className="w-4 h-4" />Review History</h4>
                    <div className="space-y-2">
                      {selected.review_comments.map(function(rc, idx) {
                        return (
                          <div key={idx} className="bg-muted/50 rounded-lg p-3 text-sm">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium">{rc.reviewer_name}</span>
                              <Badge variant="outline" className="text-xs">{rc.action === 'approve' ? 'Approved' : rc.action === 'reject' ? 'Rejected' : 'Sent Back'}</Badge>
                            </div>
                            {rc.comment && <p className="text-muted-foreground">{rc.comment}</p>}
                            <p className="text-xs text-muted-foreground mt-1">{new Date(rc.created_at).toLocaleString()}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {selected.can_review && (
                  <div className="border-t pt-4 space-y-3">
                    <Textarea value={comment} onChange={function(e) { setComment(e.target.value); }} placeholder="Add a comment (optional)..." rows={2} />
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={function() { doReview('send_back'); }} disabled={busy} className="flex items-center gap-2"><RotateCcw className="w-4 h-4" />Send Back</Button>
                      <Button variant="destructive" onClick={function() { doReview('reject'); }} disabled={busy}><XCircle className="w-4 h-4 mr-1" />Reject</Button>
                      <Button className="bg-green-600 hover:bg-green-700" onClick={function() { doReview('approve'); }} disabled={busy}><CheckCircle className="w-4 h-4 mr-1" />Approve</Button>
                    </div>
                  </div>
                )}
                <div className="border-t pt-4 space-y-2">
                  {/* Create Task from selected text - only shows when text is selected */}
                  {selectedText && (
                    <Button 
                      variant="outline" 
                      onClick={openCreateTaskFromTextDialog} 
                      className="w-full border-[#4A9CC7] text-[#4A9CC7] hover:bg-[#4A9CC7]/10"
                      data-testid="create-task-from-selection-btn"
                    >
                      <ListTodo className="w-4 h-4 mr-2" />
                      Create Task from Selected Text
                    </Button>
                  )}
                  <Button variant="outline" onClick={function() { openCreateTask(selected, ''); }} className="w-full"><ListTodo className="w-4 h-4 mr-2" />Create Task from This Submission</Button>
                </div>
              </div>
            )}
            <DialogFooter><Button variant="outline" onClick={function() { setReviewOpen(false); }}>Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><ListTodo className="w-5 h-5 text-[#4A9CC7]" />Create Task from Submission</DialogTitle>
              <DialogDescription>Create a task in a workspace shared with the submitter</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Task Title *</Label>
                <Input value={taskTitle} onChange={function(e) { setTaskTitle(e.target.value); }} placeholder="Enter task title" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={taskDescription} onChange={function(e) { setTaskDescription(e.target.value); }} placeholder="Add task description..." rows={3} />
              </div>
              <div className="space-y-2">
                <Label>Workspace *</Label>
                <Select value={selectedWorkspace || "_placeholder_"} onValueChange={function(v) { if (v !== "_placeholder_") { setSelectedWorkspace(v); loadWorkspaceMembers(v); } setSelectedProject(''); setAssignedTo(''); }}>
                  <SelectTrigger><SelectValue placeholder="Select a shared workspace" /></SelectTrigger>
                  <SelectContent>
                    {commonWorkspaces.length === 0 ? <SelectItem value="_no_ws_" disabled>No common workspaces found</SelectItem> : commonWorkspaces.map(function(ws) { return <SelectItem key={ws.workspace_id} value={ws.workspace_id}>{ws.name}</SelectItem>; })}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Only workspaces where both you and the submitter are members</p>
              </div>
              {selectedWorkspace && (
                <div className="space-y-2">
                  <Label>Project (optional)</Label>
                  <Select value={selectedProject || "_none_"} onValueChange={function(v) { setSelectedProject(v === "_none_" ? "" : v); }}>
                    <SelectTrigger><SelectValue placeholder="Select a project" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">No project</SelectItem>
                      {(commonWorkspaces.find(function(w) { return w.workspace_id === selectedWorkspace; }) || {}).projects && (commonWorkspaces.find(function(w) { return w.workspace_id === selectedWorkspace; }) || {}).projects.map(function(p) { return <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>; })}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input type="date" value={taskDueDate} onChange={function(e) { setTaskDueDate(e.target.value); }} />
              </div>
              <div className="space-y-2">
                <Label>Assign To</Label>
                <Select value={assignedTo || "_self_"} onValueChange={function(v) { setAssignedTo(v === "_self_" ? "" : v); }}>
                  <SelectTrigger><SelectValue placeholder="Assign to..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_self_">Assign to me</SelectItem>
                    {selected && <SelectItem value={selected.submitted_by}>{selected.submitted_by_name} (Submitter)</SelectItem>}
                    {selected && selected.approver_id && selected.approver_id !== selected.submitted_by && <SelectItem value={selected.approver_id}>{selected.approver_name} (Approver)</SelectItem>}
                    {workspaceMembers.filter(function(m) { return m.user_id !== selected?.submitted_by && m.user_id !== selected?.approver_id; }).map(function(m) { return <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email}</SelectItem>; })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={function() { setTaskDialogOpen(false); }}>Cancel</Button>
              <Button onClick={handleCreateTask} disabled={busy || !taskTitle.trim() || !selectedWorkspace} className="bg-[#4A9CC7] hover:bg-[#3d8ab5]">Create Task</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </AppLayout>
  );
}
